import React, { useState } from 'react';
import { ExpenseCategory, EXPENSE_TAGS } from '../types/models';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

interface ExpenseTagSelectorProps {
  selectedCategory?: ExpenseCategory;
  selectedTag?: string;
  onCategoryChange: (category: ExpenseCategory) => void;
  onTagChange: (tag: string) => void;
  className?: string;
}

const ExpenseTagSelector: React.FC<ExpenseTagSelectorProps> = ({
  selectedCategory,
  selectedTag,
  onCategoryChange,
  onTagChange,
  className = '',
}) => {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);

  // Categories sorted alphabetically (A-Z)
  const categories: { key: ExpenseCategory; label: string }[] = [
    { key: 'accessories', label: 'Accessories' },
    { key: 'clothing', label: 'Clothing' },
    { key: 'custom', label: 'Custom (Flexible)' },
    { key: 'entertainment', label: 'Entertainment' },
    { key: 'fuel', label: 'Fuel / Gas' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'lease-payment', label: 'Lease Payment' },
    { key: 'loan-interest', label: 'Loan Interest' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'meals', label: 'Meals / Snacks' },
    { key: 'miscellaneous', label: 'Miscellaneous' },
    { key: 'phone-data', label: 'Phone & Data' },
    { key: 'tax-services', label: 'Tax Services' },
    { key: 'vehicle-upkeep', label: 'Vehicle Upkeep' },
  ];

  const availableTags = selectedCategory ? EXPENSE_TAGS[selectedCategory] : [];
  const selectedCategoryLabel =
    categories.find(c => c.key === selectedCategory)?.label || 'Select Category';
  const selectedTagData = availableTags.find(t => t.name === selectedTag);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Category Selector */}
      <div>
        <label className="label">Expense Category</label>
        <div className="relative">
          <button
            type="button"
            className="input flex items-center justify-between w-full"
            onClick={() => setCategoryOpen(!categoryOpen)}
          >
            <span className={selectedCategory ? 'text-gray-900' : 'text-gray-500'}>
              {selectedCategoryLabel}
            </span>
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          </button>

          {categoryOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
              {categories.map(category => (
                <button
                  key={category.key}
                  type="button"
                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between ${
                    selectedCategory === category.key
                      ? 'bg-primary-50 text-primary-900'
                      : 'text-gray-900'
                  }`}
                  onClick={() => {
                    onCategoryChange(category.key);
                    setCategoryOpen(false);
                    // Reset tag selection when category changes
                    if (selectedCategory !== category.key) {
                      onTagChange('');
                    }
                  }}
                >
                  <span>{category.label}</span>
                  {selectedCategory === category.key && (
                    <CheckIcon className="h-5 w-5 text-primary-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tag Selector */}
      {selectedCategory && (
        <div>
          <label className="label">Specific Tag</label>
          <div className="relative">
            <button
              type="button"
              className="input flex items-center justify-between w-full"
              onClick={() => setTagOpen(!tagOpen)}
            >
              <span className={selectedTag ? 'text-gray-900' : 'text-gray-500'}>
                {selectedTag || 'Select Tag'}
              </span>
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </button>

            {tagOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {availableTags.map(tag => (
                  <button
                    key={tag.name}
                    type="button"
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                      selectedTag === tag.name ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                    }`}
                    onClick={() => {
                      onTagChange(tag.name);
                      setTagOpen(false);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{tag.name}</span>
                        {tag.description && (
                          <p className="text-xs text-gray-500 mt-1">{tag.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {tag.gstClaimable ? (
                          <span className="badge-success">GST Claimable</span>
                        ) : (
                          <span className="badge-gray">No GST</span>
                        )}
                        {selectedTag === tag.name && (
                          <CheckIcon className="h-5 w-5 text-primary-600" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tag Info */}
      {selectedTagData && (
        <div className="p-3 bg-gray-50 rounded-md">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">GST/HST Claimable:</span>
            {selectedTagData.gstClaimable ? (
              <span className="badge-success">Yes</span>
            ) : (
              <span className="badge-gray">No</span>
            )}
          </div>
          {selectedTagData.description && (
            <p className="text-sm text-gray-600 mt-2">{selectedTagData.description}</p>
          )}
        </div>
      )}

      {/* Click outside handlers */}
      {(categoryOpen || tagOpen) && (
        <button
          type="button"
          aria-label="Close dropdowns"
          className="absolute inset-0 z-0"
          onClick={() => {
            setCategoryOpen(false);
            setTagOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ExpenseTagSelector;
